import React, { useState, useEffect, useMemo } from 'react';
import { theme } from '../../theme/theme.js';
import '../../theme/theme.css';
import DataApi from '../../api/DataApi';
import ResizableTable from '../common/ResizableTable';
import CommonModal from '../common/CommonModal';
import './css/RelatedListView.css';

const DEFAULT_RELATED_LIST_ACTIONS = ['new', 'edit', 'clone', 'delete', 'bulk_delete', 'import', 'export'];

const normalizeActions = (actions) => {
  if (Array.isArray(actions) && actions.length > 0) {
    const normalized = actions
      .map(action => String(action).toLowerCase())
      .filter(action => DEFAULT_RELATED_LIST_ACTIONS.includes(action));

    return normalized.length > 0 ? Array.from(new Set(normalized)) : [...DEFAULT_RELATED_LIST_ACTIONS];
  }

  return [...DEFAULT_RELATED_LIST_ACTIONS];
};

const RelatedListView = ({
  show,
  onHide,
  objectName,
  parentField,
  parentId,
  /** When set (including null), used for list filter instead of parentId — e.g. phone for non-id relationships */
  parentFilterValue,
  title = "Related Records",
  allowedActions = DEFAULT_RELATED_LIST_ACTIONS
}) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('createddate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedRows, setSelectedRows] = useState([]);

  const modalTab = window.currentModalTab;
  const targetObjectName = modalTab?.config?.target_object_name;
  const modalTabName = modalTab?.tabName || '';

  // Get actions from modalTab config if available, otherwise use allowedActions prop
  // Priority: modalTab.config.actions > modalTab.actions > allowedActions prop > DEFAULT_RELATED_LIST_ACTIONS
  const actionsFromConfig = modalTab?.config?.actions || modalTab?.actions || allowedActions;

  // Debug logging at component level
  console.log('🔍 RelatedListView component rendered:', {
    objectName,
    modalTabExists: !!modalTab,
    modalTabConfigActions: modalTab?.config?.actions,
    modalTabActions: modalTab?.actions,
    allowedActionsProp: allowedActions,
    actionsFromConfig
  });

  const configuredActions = useMemo(
    () => {
      const normalized = normalizeActions(actionsFromConfig);
      console.log('🔍 RelatedListView configuredActions (useMemo):', {
        actionsFromConfig,
        normalized,
        modalTabConfigActions: modalTab?.config?.actions,
        allowedActionsProp: allowedActions
      });
      return normalized;
    },
    [actionsFromConfig, allowedActions, modalTab]
  );

  const isAttachmentTab =
    targetObjectName === 'attachment' || modalTabName.toLowerCase().includes('attachment');

  const isHistoryObject = modalTab?.config?.target_is_history_object === true;

  // Initialize sorting from field config if available
  useEffect(() => {
    if (window.currentModalTab && window.currentModalTab.config && window.currentModalTab.config.field_config) {
      try {
        const fieldConfig = typeof window.currentModalTab.config.field_config === 'string'
          ? JSON.parse(window.currentModalTab.config.field_config)
          : window.currentModalTab.config.field_config;

        if (fieldConfig.sortBy) {
          setSortBy(fieldConfig.sortBy);
        }
        if (fieldConfig.sortOrder) {
          setSortOrder(fieldConfig.sortOrder.toUpperCase());
        }
      } catch (error) {
        console.warn('Error parsing field config for sorting:', error);
      }
    }
  }, []);

  const recordsPerPage = 20;

  // Fetch records from the API (no server-side sorting)
  const fetchRecords = async (page = 1, search = '', sort = '', order = '') => {
    setLoading(true);
    setError(null);

    try {
      // Build query parameters
      const queryParams = new URLSearchParams();

      // Add pagination
      queryParams.append('limit', 0);
      queryParams.append('offset', ((page - 1) * recordsPerPage).toString());

      // No server-side sorting - we'll do it locally

      // Id-based relationships: parentFilterValue omitted → use parentId. Non-id: parentFilterValue is phone etc.; null = missing
      let filterMatchValue;
      if (parentFilterValue !== undefined) {
        filterMatchValue =
          parentFilterValue !== null && parentFilterValue !== ''
            ? String(parentFilterValue)
            : null;
      } else {
        filterMatchValue = parentId || null;
      }
      if (parentField && filterMatchValue) {
        queryParams.append(`filter1`, `${parentField}:equals:${filterMatchValue}`);
      }

      // Add search filter if provided (use contains for partial match)
      if (search.trim()) {
        queryParams.append(`filter2`, `name:contains:${search}`);
      }

      // Get fields from the related list configuration if available
      let displayFields = ['id', 'name']; // Default fields

      // Check if we have field configuration from the related list
      if (window.currentModalTab && window.currentModalTab.config && window.currentModalTab.config.field_config) {
        try {
          const fieldConfig = typeof window.currentModalTab.config.field_config === 'string'
            ? JSON.parse(window.currentModalTab.config.field_config)
            : window.currentModalTab.config.field_config;

          if (fieldConfig.fields && Array.isArray(fieldConfig.fields)) {
            displayFields = fieldConfig.fields;
            console.log('Using field config from related list:', displayFields);
          }
        } catch (error) {
          console.warn('Error parsing field config, using default fields:', error);
        }
      }

      // Add fields parameter to match related list display
      queryParams.append('fields', displayFields.join(','));

      // Use DataApi to fetch records
      const dataApi = new DataApi(`object/list/${objectName}`);
      const response = await dataApi.fetchAll(queryParams.toString());

      if (response.data && response.data.success) {
        // Transform the API response to match the expected structure
        const transformedRecords = (response.data.data || []).map(record => {
          console.log('Original record:', record);

          // Convert the flat object structure to fields array structure
          const fields = Object.keys(record).map(fieldName => {
            const fieldData = record[fieldName];

            // If the field is already an object with value, label, etc.
            if (fieldData && typeof fieldData === 'object' && fieldData.value !== undefined) {
              return {
                fieldName: fieldName,
                value: fieldData.value,
                label: fieldData.label || fieldName,
                type: fieldData.type || inferFieldType(fieldName),
                displayValue: fieldData.displayValue,
                referenceObject: fieldData.referenceObject,
                isReference: fieldData.isReference
              };
            } else {
              // Fallback for simple values
              return {
                fieldName: fieldName,
                value: fieldData,
                label: fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace(/([A-Z])/g, ' $1'),
                type: inferFieldType(fieldName)
              };
            }
          });

          const normalizedId =
            (record.id && typeof record.id === 'object' ? record.id.value : record.id) ??
            record.Id ??
            record.ID ??
            null;

          const transformedRecord = {
            id: normalizedId,
            fields: fields
          };

          console.log('Transformed record:', transformedRecord);
          return transformedRecord;
        });

        setRecords(transformedRecords);
        setError(null);
      } else {
        setError(response.data?.message || 'Failed to fetch records');
        setRecords([]);
      }
    } catch (error) {
      console.error('Error fetching records:', error);
      setError(error.message || 'An error occurred while fetching records');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  // Infer field type based on field name
  const inferFieldType = (fieldName) => {
    const lower = fieldName.toLowerCase();

    // Handle UUID fields (fields ending with 'id' or specific UUID patterns)
    if (lower.endsWith('id') || ['createdbyid', 'lastmodifiedbyid', 'ownerid', 'department', 'accountid', 'contactid'].includes(lower)) {
      return 'uuid';
    }

    if (lower.includes('date') && !lower.includes('created') && !lower.includes('modified')) {
      return 'date';
    }
    if (lower.includes('created') || lower.includes('modified')) {
      return 'datetime';
    }
    if (lower.includes('amount') || lower.includes('price') || lower.includes('cost')) {
      return 'currency';
    }
    if (lower.includes('email')) {
      return 'email';
    }
    if (lower.includes('phone') || lower.includes('mobile')) {
      return 'phone';
    }
    if (lower.includes('url') || lower.includes('website')) {
      return 'url';
    }
    return 'text';
  };

  // Filter and sort records locally
  const getFilteredAndSortedRecords = () => {
    let filteredRecords = records;

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filteredRecords = records.filter(record => {
        return record.fields.some(field => {
          if (!field.value) return false;

          const fieldValue = String(field.value).toLowerCase();
          const fieldLabel = String(field.label || field.fieldName || '').toLowerCase();

          return fieldValue.includes(searchLower) || fieldLabel.includes(searchLower);
        });
      });
    }

    // Apply local sorting
    if (sortBy && sortOrder) {
      filteredRecords = [...filteredRecords].sort((a, b) => {
        let aValue, bValue;

        // Handle ID field specially
        if (sortBy === 'id') {
          const aIdField = a.fields?.find(field => field.fieldName === 'id');
          const bIdField = b.fields?.find(field => field.fieldName === 'id');
          aValue = aIdField?.value || '';
          bValue = bIdField?.value || '';
        } else {
          // Handle other fields
          const aField = a.fields?.find(field => field.fieldName === sortBy);
          const bField = b.fields?.find(field => field.fieldName === sortBy);

          // Use displayValue for lookup fields, otherwise use value
          aValue = aField?.displayValue || aField?.value || '';
          bValue = bField?.displayValue || bField?.value || '';
        }

        // Handle date fields
        const aField = a.fields?.find(field => field.fieldName === sortBy);
        const bField = b.fields?.find(field => field.fieldName === sortBy);

        if (aField?.type === 'date' || aField?.type === 'datetime' ||
          bField?.type === 'date' || bField?.type === 'datetime') {
          // Convert to Date objects for comparison
          const aDate = new Date(aValue);
          const bDate = new Date(bValue);

          if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
            aValue = aDate.getTime();
            bValue = bDate.getTime();
          } else {
            // If dates are invalid, fall back to string comparison
            aValue = String(aValue).toLowerCase();
            bValue = String(bValue).toLowerCase();
          }
        } else {
          // Convert to strings for comparison
          aValue = String(aValue).toLowerCase();
          bValue = String(bValue).toLowerCase();

          // Handle numeric values
          const aNum = parseFloat(aValue);
          const bNum = parseFloat(bValue);
          if (!isNaN(aNum) && !isNaN(bNum)) {
            aValue = aNum;
            bValue = bNum;
          }
        }

        // Sort based on order
        if (sortOrder === 'asc') {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
      });
    }

    return filteredRecords;
  };

  // Handle sorting
  const handleSort = (field) => {
    const newOrder = sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortBy(field);
    setSortOrder(newOrder);
  };

  // Handle search
  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  // Fetch records when component mounts or dependencies change
  useEffect(() => {
    if (show && objectName) {
      fetchRecords(1, searchTerm, sortBy, sortOrder);
    }
  }, [show, objectName, parentField, parentId]);



  // Prepare data for ResizableTable
  const prepareTableData = () => {
    const filteredAndSortedRecords = getFilteredAndSortedRecords();

    // Show all records without pagination
    return filteredAndSortedRecords.map(record => {
      const tableRecord = {
        id: record.id,
        objectName: objectName,
        __recordId: record.id
      };

      // Add all fields to the record
      record.fields.forEach(field => {
        tableRecord[field.fieldName] = field;
      });

      return tableRecord;
    });
  };

  // Prepare columns for ResizableTable
  const prepareTableColumns = () => {
    if (records.length === 0) return [];

    const columns = [];

    records[0].fields
      .filter(field => field.fieldName?.toLowerCase() !== 'id')
      .forEach(field => {
        columns.push({
          field: field.fieldName,
          label: field.label || field.fieldName,
          sortable: true
        });
      });

    return columns;
  };

  // Dynamic actions configuration
  const getDynamicActions = () => {
    console.log('🔍 RelatedListView getDynamicActions:', {
      configuredActions,
      modalTabConfig: modalTab?.config?.actions,
      isAttachmentTab,
      isHistoryObject
    });

    if (isAttachmentTab) {
      const actions = [
        {
          label: 'View',
          icon: 'fas fa-eye',
          action: 'view'
        },
        {
          label: 'Download',
          icon: 'fas fa-download',
          action: 'download'
        }
      ];

      if (!isHistoryObject && configuredActions.includes('delete')) {
        actions.push({
          label: 'Delete',
          icon: 'fas fa-trash',
          action: 'delete'
        });
      }

      return actions;
    }

    if (isHistoryObject) {
      return [];
    }

    const actions = [
      {
        label: 'View',
        icon: 'fas fa-eye',
        action: 'view'
      }
    ];

    if (configuredActions.includes('edit')) {
      actions.push({
        label: 'Edit',
        icon: 'fas fa-edit',
        action: 'edit'
      });
    }

    if (configuredActions.includes('clone')) {
      console.log('✅ RelatedListView: Adding clone action to actions array');
      actions.push({
        label: 'Clone',
        icon: 'fas fa-clone',
        action: 'clone'
      });
    } else {
      console.log('❌ RelatedListView: Clone action NOT included. configuredActions:', configuredActions);
    }

    if (configuredActions.includes('delete')) {
      actions.push({
        label: 'Delete',
        icon: 'fas fa-trash',
        action: 'delete'
      });
    }

    console.log('🔍 RelatedListView: Final actions array:', actions);
    return actions;
  };

  // Handle row selection changes
  const handleRowSelectionChange = (selectedIds) => {
    setSelectedRows(selectedIds);
  };

  // Refresh function for ResizableTable
  const refreshRelatedList = () => {
    console.log("$$ refreshRelatedList in RelatedListView");
    fetchRecords(1, searchTerm, sortBy, sortOrder);
  };

  // Header actions configuration
  const getHeaderActions = () => {
    const actions = [];
    const selectedCount = selectedRows.length;

    if (!isHistoryObject && configuredActions.includes('new')) {
      actions.push({
        label: isAttachmentTab ? 'Upload' : 'New',
        icon: isAttachmentTab ? 'fas fa-upload' : 'fas fa-plus',
        variant: 'primary'
      });
    }

    if (!isHistoryObject && configuredActions.includes('bulk_delete')) {
      actions.push({
        label: 'Bulk Delete',
        icon: 'fas fa-trash',
        variant: 'danger',
        disabled: selectedCount === 0
      });
    }

    if (!isHistoryObject && configuredActions.includes('import')) {
      actions.push({
        label: 'Import',
        icon: 'fas fa-file-import',
        variant: 'outline-primary'
      });
    }

    if (configuredActions.includes('export')) {
      actions.push({
        label: 'Export',
        icon: 'fas fa-download',
        variant: 'primary',
        disabled: selectedCount === 0
      });
    }

    return actions;
  };

  const dynamicActions = getDynamicActions();
  const headerActions = getHeaderActions();
  const shouldShowActions = !isHistoryObject && dynamicActions.length > 0;

  const primaryFieldName = useMemo(() => {
    if (!records || records.length === 0) {
      return 'name';
    }
    const priority = ['name', 'full_name', 'firstname', 'lastname', 'title', 'subject', 'accountname'];
    const available = records[0].fields || [];

    for (const candidate of priority) {
      const match = available.find(field => field.fieldName?.toLowerCase() === candidate);
      if (match) return match.fieldName;
    }

    const firstWithValue = available.find(field => field.value);
    return firstWithValue?.fieldName || available[0]?.fieldName || 'name';
  }, [records]);

  return (
    <CommonModal
      show={show}
      onHide={onHide}
      size="xl"
      className="related-list-view"
      title={title}
      subtitle="RELATED RECORDS"
      headerIcon={<i className="fas fa-list"></i>}
      backdrop="static"
      keyboard={true}
      showCloseButton={true}
      closeButtonText="Close"
      closeButtonVariant="secondary"
    >
      <ResizableTable
        data={prepareTableData()}
        columns={prepareTableColumns()}
        loading={loading}
        error={error}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        currentPage={1}
        totalRecords={records.length}
        recordsPerPage={records.length}
        onPageChange={() => { }}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        showSerialNumber={true}
        showActions={shouldShowActions}
        actions={dynamicActions}
        headerActions={headerActions}
        selectedRows={selectedRows}
        onRowSelectionChange={handleRowSelectionChange}
        exportFilename={`${objectName}_related_records`}
        exportSheetName={objectName}
        className="related-list-view-table"
        tableClassName="table-theme"
        emptyMessage="No related records found"
        showSearch={true}
        showPagination={false}
        searchPlaceholder="Search related records..."
        objectName={objectName}
        targetField={parentField}
        recordId={parentId}
        onRefresh={refreshRelatedList}
        recordLinkField={primaryFieldName}
        headerActionsUnifiedDropdown={true}
      />
    </CommonModal>
  );
};

export default RelatedListView;
